// The live meeting page's countdown. DISPLAY ONLY: the backend ends a meeting
// at its own deadline whatever this shows (services/subscription_service.py's
// standard_meeting_deadline / the pass and trial deadlines). A countdown is
// always derived from the server's absolute deadline and the current clock -
// never decremented - so a throttled tab, a sleeping laptop or a reconnect
// cannot make it drift or restart.

export const STANDARD_MEETING_CODE = 'MEETING_TIME_LIMIT_REACHED';
export const TIME_LIMIT_CODES = [
  'FREE_TRIAL_DURATION_REACHED',
  'MEETING_PASS_DURATION_REACHED',
  STANDARD_MEETING_CODE,
];

// The standard cap, used only until the server's own limit arrives (an older
// backend never sends one).
export const FALLBACK_LIMIT_SECONDS = 2 * 60 * 60;

export function isTimeLimitCode(code) {
  return TIME_LIMIT_CODES.includes(code);
}

function parse(value) {
  if (!value) return null;
  const ms = new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * Seconds left, or null when there is nothing to count down to (no limit and
 * no start time). `limit` is the server's `time_limit`; `startedAt` the
 * meeting's `started_at`. A meeting that has not started has its whole limit
 * ahead of it.
 */
export function secondsRemaining(limit, startedAt, nowMs) {
  const deadline = parse(limit?.deadline_at);
  if (deadline !== null) return Math.max(0, Math.ceil((deadline - nowMs) / 1000));

  const limitSeconds = Number.isFinite(limit?.limit_seconds) ? limit.limit_seconds : FALLBACK_LIMIT_SECONDS;
  const started = parse(limit?.started_at) ?? parse(startedAt);
  if (started === null) return limit ? limitSeconds : null;
  return Math.max(0, Math.ceil((started + limitSeconds * 1000 - nowMs) / 1000));
}

// The free trial lasts 10 minutes in all, so it warns at 2 minutes; every
// other meeting warns quietly at 10 minutes. All warn prominently at 1.
export function warningLevel(seconds, kind) {
  if (seconds <= 0) return 'ended';
  if (seconds <= 60) return 'one_minute';
  if (kind === 'free_trial') return seconds <= 120 ? 'two_minutes' : 'none';
  return seconds <= 10 * 60 ? 'ten_minutes' : 'none';
}

export function formatCountdown(totalSeconds) {
  const seconds = Number.isFinite(totalSeconds) ? Math.max(0, Math.floor(totalSeconds)) : 0;
  const pad = (n) => String(n).padStart(2, '0');
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${pad(h)}:${pad(m)}:${pad(s)}`;
}

export const WARNING_MESSAGES = {
  ten_minutes: '10 minutes left in this meeting.',
  two_minutes: '2 minutes left in your free meeting.',
  one_minute:
    '1 minute left - KrishvaQ assistance ends when the timer reaches 00:00. Your call itself continues.',
};

export const STANDARD_MEETING_ENDED_TITLE = 'Meeting ended';
export const STANDARD_MEETING_ENDED_MESSAGE =
  'This meeting has reached its 2-hour time limit. Your meeting data has been saved. You can start another meeting if you have available credits.';
